/**
 * UAT Test Scripts Page
 * Preview test steps from DB or extract from GCS; edit scenario-in-scope in a dialog.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  IconButton,
  Checkbox,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import ArticleIcon from '@mui/icons-material/Article';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import {
  fetchTestProcedures,
  patchScenarioInScope,
  uploadUatScopeExcel,
  downloadUatTestScriptExcel,
  downloadUatTestScriptDocx,
  downloadUatOriginalTestScriptDocx,
  fetchUatGlobalVariables,
  downloadUatGlobalVariablesExcel,
  uploadUatGlobalVariablesExcel,
} from '../../utils/uatTestScriptsService';

const REALIZE_COLOR = '#9C27B0';
const DEFAULT_SORT_FIELD = 'scope_item';
const DEFAULT_SORT_DIRECTION = 'asc';
const MAIN_TABLE_ROWS_PER_PAGE = 10;
const PREVIEW_TABLE_ROWS_PER_PAGE = 10;
const EDIT_TABLE_ROWS_PER_PAGE = 10;

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const stepRefSegments = (stepRef) =>
  String(stepRef || '')
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);

const isStrictAncestorStep = (ancestorRef, descendantRef) => {
  const ancestor = stepRefSegments(ancestorRef);
  const descendant = stepRefSegments(descendantRef);
  if (ancestor.length >= descendant.length) return false;
  return ancestor.every((part, index) => part === descendant[index]);
};

const applyScenarioScopeCascade = (rows, toggledRow, checked) => {
  const targetRef = (toggledRow?.step_ref || '').trim();
  return rows.map((row) => {
    const ref = (row.step_ref || '').trim();
    const isTarget = row.id === toggledRow.id;
    const isAncestor = isStrictAncestorStep(ref, targetRef);
    const isDescendant = isStrictAncestorStep(targetRef, ref);

    if (checked && (isTarget || isAncestor || isDescendant)) {
      return { ...row, scenario_in_scope: true };
    }
    if (!checked && (isTarget || isDescendant)) {
      return { ...row, scenario_in_scope: false };
    }
    return row;
  });
};

const cloneRowsForEdit = (rows = []) =>
  rows.map((row) => ({
    ...row,
    scenario_in_scope: Boolean(row.scenario_in_scope),
  }));

const UATTestScriptsPage = () => {
  const { projectId } = useParams();
  const currentProjectId = projectId ? parseInt(projectId, 10) : null;
  const navigate = useNavigate();
  const { project } = useOutletContext() || {};

  const [scopeItems, setScopeItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [previewDialog, setPreviewDialog] = useState({ open: false, item: null });
  const [rowsByScopeItemId, setRowsByScopeItemId] = useState({});
  const [loadingScopeItemId, setLoadingScopeItemId] = useState(null);
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [editRows, setEditRows] = useState([]);
  const [editBaselineRows, setEditBaselineRows] = useState([]);
  const [editSearchQuery, setEditSearchQuery] = useState('');
  const [editScopeFilter, setEditScopeFilter] = useState('all');
  const [editTablePage, setEditTablePage] = useState(0);
  const [scopeImportSummary, setScopeImportSummary] = useState(null);
  const [scopeImportIssue, setScopeImportIssue] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState('');
  const [downloadingExcelId, setDownloadingExcelId] = useState(null);
  const [uploadingScopeExcelId, setUploadingScopeExcelId] = useState(null);
  const [downloadingOriginalDocxId, setDownloadingOriginalDocxId] = useState(null);
  const [downloadingDocxId, setDownloadingDocxId] = useState(null);
  const [globalVariables, setGlobalVariables] = useState(null);
  const [loadingGlobalVariables, setLoadingGlobalVariables] = useState(false);
  const [downloadingGlobalVariables, setDownloadingGlobalVariables] = useState(false);
  const [uploadingGlobalVariables, setUploadingGlobalVariables] = useState(false);
  const [configDialog, setConfigDialog] = useState({ open: false, message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedModule, setSelectedModule] = useState('all');
  const [sortField, setSortField] = useState(DEFAULT_SORT_FIELD);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [mainTablePage, setMainTablePage] = useState(0);
  const [previewTablePages, setPreviewTablePages] = useState({});

  useEffect(() => {
    const fetchScopeItems = async () => {
      if (!currentProjectId) {
        setScopeItems([]);
        return;
      }
      try {
        setLoading(true);
        const items = await getScopeItemsForProject(currentProjectId);
        setScopeItems(items);
      } catch (error) {
        console.error('Error fetching scope items:', error);
        setScopeItems([]);
        showSnackbar('Error loading scope items', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchScopeItems();
  }, [currentProjectId]);

  useEffect(() => {
    const loadGlobalVariables = async () => {
      if (!currentProjectId) {
        setGlobalVariables(null);
        return;
      }
      setLoadingGlobalVariables(true);
      try {
        const data = await fetchUatGlobalVariables(currentProjectId);
        if (data.success) {
          setGlobalVariables(data);
        } else {
          setGlobalVariables(null);
        }
      } catch (error) {
        console.error('Error loading global variables:', error);
        setGlobalVariables(null);
      } finally {
        setLoadingGlobalVariables(false);
      }
    };
    loadGlobalVariables();
  }, [currentProjectId]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const openEditionVersionDialog = useCallback((message) => {
    setConfigDialog({ open: true, message });
  }, []);

  const ensureEditionAndVersionFromProject = useCallback(() => {
    const edition = (project?.edition || '').trim();
    const version = (project?.version || '').trim();
    if (!edition && !version) {
      openEditionVersionDialog(
        'Please go to Project Overview → Project Scope and add Edition and Version before loading test scripts from storage.'
      );
      return false;
    }
    if (!edition) {
      openEditionVersionDialog(
        'Please go to Project Overview → Project Scope and add Edition before loading test scripts from storage.'
      );
      return false;
    }
    if (!version) {
      openEditionVersionDialog(
        'Please go to Project Overview → Project Scope and add Version before loading test scripts from storage.'
      );
      return false;
    }
    return true;
  }, [project?.edition, project?.version, openEditionVersionDialog]);

  const handleApiEditionVersionHint = useCallback((data) => {
    if (
      data?.missing_edition ||
      data?.missing_version ||
      data?.invalid_edition ||
      data?.invalid_version
    ) {
      setConfigDialog({
        open: true,
        message:
          data.error ||
          'Please configure Edition and Version in Project Overview → Project Scope.',
      });
      return true;
    }
    return false;
  }, []);

  const loadProceduresForScopeItem = useCallback(async (scopeItemId) => {
    if (!currentProjectId) return null;
    if (!ensureEditionAndVersionFromProject()) return null;
    setLoadingScopeItemId(scopeItemId);
    try {
      const data = await fetchTestProcedures(currentProjectId, scopeItemId);
      if (data.success && Array.isArray(data.rows)) {
        setRowsByScopeItemId((prev) => ({ ...prev, [scopeItemId]: data.rows }));
        if (data.source === 'extracted') {
          showSnackbar('Test procedures loaded from SAP test script document.', 'success');
        }
        return data.rows;
      } else {
        if (handleApiEditionVersionHint(data)) {
          setPreviewDialog({ open: false, item: null });
          return null;
        }
        const extra = data.error_detail ? ` — ${data.error_detail}` : '';
        showSnackbar((data.error || 'Could not load test procedures') + extra, 'error');
        setPreviewDialog({ open: false, item: null });
        return null;
      }
    } catch (e) {
      console.error(e);
      showSnackbar('Error loading test procedures', 'error');
      setPreviewDialog({ open: false, item: null });
      return null;
    } finally {
      setLoadingScopeItemId(null);
    }
  }, [currentProjectId, ensureEditionAndVersionFromProject, handleApiEditionVersionHint]);

  const handleOpenPreviewDialog = (item) => {
    const id = item.id;
    if (!ensureEditionAndVersionFromProject()) {
      setPreviewDialog({ open: false, item: null });
      return;
    }
    setPreviewDialog({ open: true, item });
    if (!rowsByScopeItemId[id]) {
      loadProceduresForScopeItem(id);
    }
  };

  const handleClosePreviewDialog = () => {
    setPreviewDialog({ open: false, item: null });
  };

  const handleDownloadExcel = async (event, item) => {
    event.stopPropagation();
    if (!currentProjectId || !item?.id) return;
    if (!ensureEditionAndVersionFromProject()) return;
    const scopeItemId = item.id;
    setDownloadingExcelId(scopeItemId);
    try {
      const result = await downloadUatTestScriptExcel(
        currentProjectId,
        scopeItemId,
        item.scope_item
      );
      if (result.ok) {
        showSnackbar('Excel downloaded.', 'success');
      } else if (
        result.missing_edition ||
        result.missing_version ||
        result.invalid_edition ||
        result.invalid_version
      ) {
        openEditionVersionDialog(result.error || 'Please configure Edition and Version in Project Scope.');
      } else {
        showSnackbar(result.error || 'Could not download Excel', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnackbar('Error downloading Excel', 'error');
    } finally {
      setDownloadingExcelId(null);
    }
  };

  const handleUploadScopeExcel = async (event, item) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !item?.id) return;
    if (!currentProjectId) return;
    if (!ensureEditionAndVersionFromProject()) return;

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      showSnackbar('Please upload an .xlsx scope Excel file.', 'error');
      return;
    }

    setUploadingScopeExcelId(item.id);
    setEditError('');
    setScopeImportSummary(null);
    setScopeImportIssue(null);
    try {
      const existingRows = rowsByScopeItemId[item.id] || (await loadProceduresForScopeItem(item.id));
      if (!existingRows || existingRows.length === 0) {
        showSnackbar('No test procedure rows are available for this scope item.', 'error');
        return;
      }

      const data = await uploadUatScopeExcel(currentProjectId, item.id, file);
      if (!data.success) {
        if (handleApiEditionVersionHint(data)) return;

        const baselineRows = cloneRowsForEdit(existingRows);
        setEditDialog({ open: true, item });
        setEditRows(baselineRows);
        setEditBaselineRows(baselineRows);
        setEditSearchQuery('');
        setEditScopeFilter('all');
        setEditTablePage(0);
        setPreviewDialog({ open: false, item: null });
        setScopeImportIssue({
          filename: file.name,
          message: data.error || 'Could not import scope Excel.',
          invalidValues: data.invalid_values || [],
          unmatchedSteps: data.unmatched_steps || [],
          missingSteps: data.missing_steps || [],
        });

        const invalidCount = data.invalid_values?.length || 0;
        const unmatchedCount = data.unmatched_steps?.length || 0;
        const extra = invalidCount
          ? ` ${invalidCount} invalid value${invalidCount === 1 ? '' : 's'}.`
          : unmatchedCount
            ? ` ${unmatchedCount} unmatched step${unmatchedCount === 1 ? '' : 's'}.`
            : '';
        showSnackbar(`${data.error || 'Could not import scope Excel.'}${extra}`, 'error');
        return;
      }

      const baselineRows = cloneRowsForEdit(existingRows);
      const importedById = new Map((data.rows || []).map((row) => [row.row_id, Boolean(row.scenario_in_scope)]));
      const baselineById = new Map(baselineRows.map((row) => [row.id, Boolean(row.scenario_in_scope)]));
      const importedRows = baselineRows.map((row) => (
        importedById.has(row.id)
          ? { ...row, scenario_in_scope: importedById.get(row.id) }
          : row
      ));
      const importedChangedCount = importedRows.filter((row) => {
        if (!baselineById.has(row.id)) return false;
        return Boolean(row.scenario_in_scope) !== baselineById.get(row.id);
      }).length;

      setEditDialog({ open: true, item });
      setEditRows(importedRows);
      setEditBaselineRows(baselineRows);
      setEditSearchQuery('');
      setEditScopeFilter('all');
      setEditTablePage(0);
      setPreviewDialog({ open: false, item: null });
      setScopeImportIssue(null);
      setScopeImportSummary({
        filename: data.filename || file.name,
        message: data.message || 'Scope Excel imported.',
        matchedCount: data.matched_count || 0,
        inScopeCount: data.in_scope_count || 0,
        outScopeCount: data.out_scope_count || 0,
        changedCount: importedChangedCount,
        unmatchedCount: data.unmatched_steps?.length || 0,
        missingCount: data.missing_steps?.length || 0,
      });

      const warnings = [];
      if (data.unmatched_steps?.length) {
        warnings.push(`${data.unmatched_steps.length} unmatched`);
      }
      if (data.missing_steps?.length) {
        warnings.push(`${data.missing_steps.length} missing`);
      }
      const warningText = warnings.length ? ` (${warnings.join(', ')})` : '';
      showSnackbar(`${data.message || 'Scope Excel imported.'}${warningText}. Review and save changes.`, 'success');
    } catch (e) {
      console.error(e);
      showSnackbar('Error uploading scope Excel', 'error');
    } finally {
      setUploadingScopeExcelId(null);
    }
  };

  const handleDownloadDocx = async (event, item) => {
    event.stopPropagation();
    if (!currentProjectId || !item?.id) return;
    if (!ensureEditionAndVersionFromProject()) return;
    const scopeItemId = item.id;
    setDownloadingDocxId(scopeItemId);
    try {
      const result = await downloadUatTestScriptDocx(
        currentProjectId,
        scopeItemId,
        item.scope_item,
        project?.name
      );
      if (result.ok) {
        showSnackbar('Test script document downloaded.', 'success');
      } else if (
        result.missing_edition ||
        result.missing_version ||
        result.invalid_edition ||
        result.invalid_version
      ) {
        openEditionVersionDialog(result.error || 'Please configure Edition and Version in Project Scope.');
      } else {
        showSnackbar(result.error || 'Could not download test script document', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnackbar('Error downloading test script document', 'error');
    } finally {
      setDownloadingDocxId(null);
    }
  };

  const handleDownloadOriginalDocx = async (event, item) => {
    event.stopPropagation();
    if (!currentProjectId || !item?.id) return;
    if (!ensureEditionAndVersionFromProject()) return;
    const scopeItemId = item.id;
    setDownloadingOriginalDocxId(scopeItemId);
    try {
      const result = await downloadUatOriginalTestScriptDocx(
        currentProjectId,
        scopeItemId,
        item.scope_item
      );
      if (result.ok) {
        showSnackbar('Original test script document downloaded.', 'success');
      } else if (
        result.missing_edition ||
        result.missing_version ||
        result.invalid_edition ||
        result.invalid_version
      ) {
        openEditionVersionDialog(result.error || 'Please configure Edition and Version in Project Scope.');
      } else {
        showSnackbar(result.error || 'Could not download original test script document', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnackbar('Error downloading original test script document', 'error');
    } finally {
      setDownloadingOriginalDocxId(null);
    }
  };

  const handleDownloadGlobalVariables = async () => {
    if (!currentProjectId) return;
    setDownloadingGlobalVariables(true);
    try {
      const result = await downloadUatGlobalVariablesExcel(currentProjectId, project?.name);
      if (result.ok) {
        showSnackbar('Global variables Excel downloaded.', 'success');
      } else {
        showSnackbar(result.error || 'Could not download global variables Excel', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnackbar('Error downloading global variables Excel', 'error');
    } finally {
      setDownloadingGlobalVariables(false);
    }
  };

  const handleUploadGlobalVariables = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !currentProjectId) return;

    setUploadingGlobalVariables(true);
    try {
      const data = await uploadUatGlobalVariablesExcel(currentProjectId, file);
      if (data.success) {
        setGlobalVariables(data);
        const count = data.variables_count || 0;
        showSnackbar(
          `Global variables uploaded. ${count} non-empty value${count === 1 ? '' : 's'} saved.`,
          'success'
        );
      } else {
        showSnackbar(data.error || 'Could not upload global variables Excel', 'error');
      }
    } catch (e) {
      console.error(e);
      showSnackbar('Error uploading global variables Excel', 'error');
    } finally {
      setUploadingGlobalVariables(false);
    }
  };

  const handleOpenEditDialog = async (event, item) => {
    event?.stopPropagation();
    if (!currentProjectId || !item?.id) return;
    if (!ensureEditionAndVersionFromProject()) return;

    setEditError('');
    setEditSearchQuery('');
    setEditScopeFilter('all');
    setEditTablePage(0);
    setScopeImportSummary(null);
    setScopeImportIssue(null);
    setPreviewDialog({ open: false, item: null });
    setEditDialog({ open: true, item });

    const existingRows = rowsByScopeItemId[item.id];
    if (existingRows) {
      const clonedRows = cloneRowsForEdit(existingRows);
      setEditRows(clonedRows);
      setEditBaselineRows(clonedRows);
      return;
    }

    setEditRows([]);
    setEditBaselineRows([]);
    const loadedRows = await loadProceduresForScopeItem(item.id);
    if (loadedRows) {
      const clonedRows = cloneRowsForEdit(loadedRows);
      setEditRows(clonedRows);
      setEditBaselineRows(clonedRows);
    }
  };

  const handleCloseEditDialog = () => {
    if (savingEdit) return;
    setEditDialog({ open: false, item: null });
    setEditRows([]);
    setEditBaselineRows([]);
    setEditSearchQuery('');
    setEditScopeFilter('all');
    setEditTablePage(0);
    setScopeImportSummary(null);
    setScopeImportIssue(null);
    setEditError('');
  };

  const handleEditScenarioChange = (row, checked) => {
    if (savingEdit) return;
    setEditRows((currentRows) => applyScenarioScopeCascade(currentRows, row, checked));
  };

  const handleBulkEditScenarioChange = (checked) => {
    if (savingEdit || filteredEditRows.length === 0) return;
    const targetIds = new Set(filteredEditRows.map((row) => row.id));
    setEditRows((currentRows) =>
      currentRows.map((row) =>
        targetIds.has(row.id)
          ? { ...row, scenario_in_scope: checked }
          : row
      )
    );
  };

  const handleResetEditChanges = () => {
    if (savingEdit || editChangedCount === 0) return;
    setEditRows(cloneRowsForEdit(editBaselineRows));
    setEditScopeFilter('all');
    setEditTablePage(0);
    setScopeImportSummary(null);
    setScopeImportIssue(null);
    setEditError('');
    showSnackbar('Unsaved scope changes reset.', 'info');
  };

  const handleSaveEditDialog = async () => {
    const item = editDialog.item;
    if (!currentProjectId || !item?.id) return;

    const desiredById = new Map(editRows.map((row) => [row.id, Boolean(row.scenario_in_scope)]));
    let latestRows = rowsByScopeItemId[item.id] || editRows;
    const maxAttempts = editRows.length * 2 + 5;

    setSavingEdit(true);
    setEditError('');
    try {
      let completed = false;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        let nextMismatch = null;
        for (const row of latestRows) {
          if (desiredById.has(row.id) && Boolean(row.scenario_in_scope) !== desiredById.get(row.id)) {
            nextMismatch = row;
            break;
          }
        }

        if (!nextMismatch) {
          completed = true;
          break;
        }

        const data = await patchScenarioInScope(
          nextMismatch.id,
          currentProjectId,
          desiredById.get(nextMismatch.id)
        );

        if (!data.success) {
          throw new Error(data.error || 'Could not save test script changes');
        }

        if (Array.isArray(data.rows) && data.rows.length > 0) {
          latestRows = data.rows;
        } else if (data.row) {
          latestRows = latestRows.map((row) => (row.id === data.row.id ? { ...row, ...data.row } : row));
        }
      }

      if (completed) {
        setRowsByScopeItemId((prev) => ({
          ...prev,
          [item.id]: latestRows,
        }));
        showSnackbar('Test script scenario selections saved.', 'success');
        setEditDialog({ open: false, item: null });
        setEditRows([]);
        setEditBaselineRows([]);
        setEditSearchQuery('');
        setEditScopeFilter('all');
        setEditTablePage(0);
        setScopeImportSummary(null);
        setScopeImportIssue(null);
        setEditError('');
        return;
      }

      throw new Error('Could not reconcile all scenario selections. Please review and try again.');
    } catch (e) {
      console.error(e);
      setEditError(e.message || 'Error saving test script changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const moduleOptions = useMemo(
    () => [...new Set(scopeItems.map((item) => item.module).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [scopeItems]
  );

  const filteredScopeItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filteredItems = scopeItems.filter((item) => {
      const matchesModule = selectedModule === 'all' || item.module === selectedModule;
      const matchesQuery =
        !normalizedQuery ||
        [item.module, item.scope_item, item.scope_item_description]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery));

      return matchesModule && matchesQuery;
    });

    return [...filteredItems].sort((a, b) => {
      const left = (a[sortField] || '').toString().toLowerCase();
      const right = (b[sortField] || '').toString().toLowerCase();
      const comparison = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });

      if (comparison !== 0) {
        return sortDirection === 'asc' ? comparison : -comparison;
      }

      return (a.scope_item || '').localeCompare(b.scope_item || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
  }, [scopeItems, searchQuery, selectedModule, sortField, sortDirection]);

  const paginatedScopeItems = useMemo(
    () =>
      filteredScopeItems.slice(
        mainTablePage * MAIN_TABLE_ROWS_PER_PAGE,
        mainTablePage * MAIN_TABLE_ROWS_PER_PAGE + MAIN_TABLE_ROWS_PER_PAGE
      ),
    [filteredScopeItems, mainTablePage]
  );

  useEffect(() => {
    setMainTablePage(0);
  }, [searchQuery, selectedModule, sortField, sortDirection]);

  useEffect(() => {
    if (
      previewDialog.item?.id &&
      !filteredScopeItems.some((item) => item.id === previewDialog.item.id)
    ) {
      setPreviewDialog({ open: false, item: null });
    }
  }, [previewDialog.item?.id, filteredScopeItems]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedModule('all');
    setSortField(DEFAULT_SORT_FIELD);
    setSortDirection(DEFAULT_SORT_DIRECTION);
  };

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortField(field);
    setSortDirection('asc');
  };

  const handleMainTablePageChange = (_event, newPage) => {
    setMainTablePage(newPage);
  };

  const handlePreviewTablePageChange = (scopeItemId, newPage) => {
    setPreviewTablePages((currentPages) => ({
      ...currentPages,
      [scopeItemId]: newPage,
    }));
  };

  const editSelectedCount = useMemo(
    () => editRows.filter((row) => Boolean(row.scenario_in_scope)).length,
    [editRows]
  );

  const editBaselineById = useMemo(
    () => new Map(editBaselineRows.map((row) => [row.id, Boolean(row.scenario_in_scope)])),
    [editBaselineRows]
  );

  const editChangedCount = useMemo(
    () =>
      editRows.filter((row) => {
        if (!editBaselineById.has(row.id)) return false;
        return Boolean(row.scenario_in_scope) !== editBaselineById.get(row.id);
      }).length,
    [editRows, editBaselineById]
  );

  const filteredEditRows = useMemo(() => {
    const normalizedQuery = editSearchQuery.trim().toLowerCase();

    return editRows.filter((row) => {
      const matchesQuery =
        !normalizedQuery ||
        [row.step_ref, row.test_procedure]
          .filter(Boolean)
          .some((value) => value.toString().toLowerCase().includes(normalizedQuery));
      const matchesScope =
        editScopeFilter === 'all' ||
        (editScopeFilter === 'in' && Boolean(row.scenario_in_scope)) ||
        (editScopeFilter === 'out' && !Boolean(row.scenario_in_scope)) ||
        (
          editScopeFilter === 'changed' &&
          editBaselineById.has(row.id) &&
          Boolean(row.scenario_in_scope) !== editBaselineById.get(row.id)
        );

      return matchesQuery && matchesScope;
    });
  }, [editRows, editSearchQuery, editScopeFilter, editBaselineById]);

  const paginatedEditRows = useMemo(
    () =>
      filteredEditRows.slice(
        editTablePage * EDIT_TABLE_ROWS_PER_PAGE,
        editTablePage * EDIT_TABLE_ROWS_PER_PAGE + EDIT_TABLE_ROWS_PER_PAGE
      ),
    [filteredEditRows, editTablePage]
  );

  const filteredEditSelectedCount = useMemo(
    () => filteredEditRows.filter((row) => Boolean(row.scenario_in_scope)).length,
    [filteredEditRows]
  );

  useEffect(() => {
    setEditTablePage(0);
  }, [editSearchQuery, editScopeFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredEditRows.length / EDIT_TABLE_ROWS_PER_PAGE) - 1);
    setEditTablePage((currentPage) => Math.min(currentPage, maxPage));
  }, [filteredEditRows.length]);

  const globalVariableCount = globalVariables?.variables_count || 0;
  const globalVariableStatus = loadingGlobalVariables
    ? 'Checking saved values...'
    : globalVariables?.exists
      ? `${globalVariableCount} saved value${globalVariableCount === 1 ? '' : 's'}`
      : 'No values uploaded';

  const editDialogLoading = Boolean(
    editDialog.open &&
      editDialog.item?.id &&
      loadingScopeItemId === editDialog.item.id &&
      editRows.length === 0
  );

  const previewItem = previewDialog.item;
  const previewAllRows = previewItem?.id ? rowsByScopeItemId[previewItem.id] : null;
  const previewDialogLoading = Boolean(
    previewDialog.open &&
      previewItem?.id &&
      loadingScopeItemId === previewItem.id &&
      !previewAllRows
  );
  const previewPage = previewItem?.id ? previewTablePages[previewItem.id] || 0 : 0;
  const previewRows = Array.isArray(previewAllRows)
    ? previewAllRows.slice(
        previewPage * PREVIEW_TABLE_ROWS_PER_PAGE,
        previewPage * PREVIEW_TABLE_ROWS_PER_PAGE + PREVIEW_TABLE_ROWS_PER_PAGE
      )
    : [];
  const previewInScopeCount = Array.isArray(previewAllRows)
    ? previewAllRows.filter((row) => Boolean(row.scenario_in_scope)).length
    : 0;
  const previewOutScopeCount = Array.isArray(previewAllRows)
    ? previewAllRows.length - previewInScopeCount
    : 0;

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <DescriptionIcon sx={{ color: REALIZE_COLOR }} />
            Test Scripts
          </SectionTitle>
        </Box>

        <Typography variant="body1" color="text.secondary" paragraph>
          Select a scope item to view test procedure steps. Scenario in scope can be marked per step; data is saved for this project.
        </Typography>

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            bgcolor: 'rgba(255,255,255,0.03)',
            borderColor: 'rgba(255,255,255,0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Global Variables Excel
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {globalVariableStatus}
                {globalVariables?.updated_at ? ` - Updated ${new Date(globalVariables.updated_at).toLocaleString()}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={
                  downloadingGlobalVariables ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DownloadIcon />
                  )
                }
                disabled={!currentProjectId || downloadingGlobalVariables || uploadingGlobalVariables}
                onClick={handleDownloadGlobalVariables}
              >
                Download
              </Button>
              <Button
                component="label"
                variant="contained"
                startIcon={
                  uploadingGlobalVariables ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <UploadIcon />
                  )
                }
                disabled={!currentProjectId || uploadingGlobalVariables || downloadingGlobalVariables}
                sx={{ bgcolor: REALIZE_COLOR, '&:hover': { bgcolor: '#7B1FA2' } }}
              >
                Upload
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleUploadGlobalVariables}
                />
              </Button>
            </Box>
          </Box>
        </Paper>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            {!currentProjectId ? (
              <Alert severity="warning">
                No project selected. Please navigate to a project first.
              </Alert>
            ) : loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress />
              </Box>
            ) : scopeItems.length === 0 ? (
              <Alert severity="info">
                No scope items found for this project. Please upload scope items in the Prepare Phase first.
              </Alert>
            ) : (
              <>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={7}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Search"
                      placeholder="Search module, scope item, or description"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="uat-module-filter-label">Module</InputLabel>
                      <Select
                        labelId="uat-module-filter-label"
                        value={selectedModule}
                        label="Module"
                        onChange={(event) => setSelectedModule(event.target.value)}
                      >
                        <MenuItem value="all">All Modules</MenuItem>
                        {moduleOptions.map((module) => (
                          <MenuItem key={module} value={module}>
                            {module}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      color="inherit"
                      onClick={handleClearFilters}
                      sx={{ height: '100%' }}
                    >
                      Clear
                    </Button>
                  </Grid>
                </Grid>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Showing {filteredScopeItems.length} of {scopeItems.length} scope items
                  </Typography>
                </Box>

                {filteredScopeItems.length === 0 ? (
                  <Alert severity="info">
                    No scope items match the current search or filter settings.
                  </Alert>
                ) : (
                  <>
                    <TableContainer
                      component={Paper}
                      variant="outlined"
                      sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}
                    >
                      <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#4472C4',
                              color: '#fff',
                              whiteSpace: 'nowrap',
                              width: 64,
                              minWidth: 64,
                              fontSize: '0.78rem',
                              position: 'sticky',
                              left: 0,
                              zIndex: 4,
                            }}
                          >
                            S.No
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#4472C4',
                              color: '#fff',
                              whiteSpace: 'nowrap',
                              minWidth: 240,
                              fontSize: '0.78rem',
                              position: 'sticky',
                              left: 64,
                              zIndex: 3,
                            }}
                          >
                            Actions
                          </TableCell>
                          <TableCell
                            sortDirection={sortField === 'module' ? sortDirection : false}
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#4472C4',
                              color: '#fff',
                              whiteSpace: 'nowrap',
                              minWidth: 120,
                              fontSize: '0.78rem',
                              '& .MuiTableSortLabel-root': { color: '#fff' },
                              '& .MuiTableSortLabel-root:hover': { color: '#E3F2FD' },
                              '& .MuiTableSortLabel-root.Mui-active': { color: '#fff' },
                              '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                            }}
                          >
                            <TableSortLabel
                              active={sortField === 'module'}
                              direction={sortField === 'module' ? sortDirection : 'asc'}
                              onClick={() => handleSortRequest('module')}
                            >
                              Module
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={sortField === 'scope_item' ? sortDirection : false}
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#4472C4',
                              color: '#fff',
                              whiteSpace: 'nowrap',
                              minWidth: 140,
                              fontSize: '0.78rem',
                              '& .MuiTableSortLabel-root': { color: '#fff' },
                              '& .MuiTableSortLabel-root:hover': { color: '#E3F2FD' },
                              '& .MuiTableSortLabel-root.Mui-active': { color: '#fff' },
                              '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                            }}
                          >
                            <TableSortLabel
                              active={sortField === 'scope_item'}
                              direction={sortField === 'scope_item' ? sortDirection : 'asc'}
                              onClick={() => handleSortRequest('scope_item')}
                            >
                              Scope Item
                            </TableSortLabel>
                          </TableCell>
                          <TableCell
                            sortDirection={sortField === 'scope_item_description' ? sortDirection : false}
                            sx={{
                              fontWeight: 700,
                              bgcolor: '#4472C4',
                              color: '#fff',
                              whiteSpace: 'nowrap',
                              minWidth: 220,
                              fontSize: '0.78rem',
                              '& .MuiTableSortLabel-root': { color: '#fff' },
                              '& .MuiTableSortLabel-root:hover': { color: '#E3F2FD' },
                              '& .MuiTableSortLabel-root.Mui-active': { color: '#fff' },
                              '& .MuiTableSortLabel-icon': { color: '#fff !important' },
                            }}
                          >
                            <TableSortLabel
                              active={sortField === 'scope_item_description'}
                              direction={sortField === 'scope_item_description' ? sortDirection : 'asc'}
                              onClick={() => handleSortRequest('scope_item_description')}
                            >
                              Description
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedScopeItems.map((item, idx) => {
                          const previewOpen = previewDialog.open && previewDialog.item?.id === item.id;
                          const rowBg = idx % 2 === 0 ? '#263238' : '#37474F';
                          const serialNumber = mainTablePage * MAIN_TABLE_ROWS_PER_PAGE + idx + 1;

                          return (
                            <React.Fragment key={item.id}>
                              <TableRow
                                hover
                                onClick={() => handleOpenPreviewDialog(item)}
                                sx={{
                                  cursor: 'pointer',
                                  bgcolor: previewOpen ? 'rgba(156,39,176,0.18)' : rowBg,
                                  '&:hover': {
                                    bgcolor: previewOpen ? 'rgba(156,39,176,0.25)' : '#546E7A',
                                  },
                                  '& > *': {
                                    borderColor: 'rgba(255,255,255,0.08)',
                                    color: '#FFFFFF',
                                  },
                                }}
                                selected={previewOpen}
                              >
                                <TableCell
                                  sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 2,
                                    bgcolor: previewOpen ? 'rgba(156,39,176,0.18)' : rowBg,
                                    borderColor: 'rgba(255,255,255,0.08)',
                                    fontWeight: 700,
                                    width: 64,
                                    minWidth: 64,
                                    textAlign: 'center',
                                  }}
                                >
                                  {serialNumber}
                                </TableCell>
                                <TableCell
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    position: 'sticky',
                                    left: 64,
                                    zIndex: 1,
                                    bgcolor: previewOpen ? 'rgba(156,39,176,0.18)' : rowBg,
                                    borderColor: 'rgba(255,255,255,0.08)',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Tooltip title="Preview test procedures">
                                      <IconButton
                                        size="small"
                                        aria-label="Preview test procedures"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenPreviewDialog(item);
                                        }}
                                        sx={{
                                          color: 'rgba(255,255,255,0.85)',
                                          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                                        }}
                                      >
                                        {previewOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit scenario scope selections">
                                      <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                                        onClick={(e) => handleOpenEditDialog(e, item)}
                                        sx={{
                                          bgcolor: REALIZE_COLOR,
                                          '&:hover': { bgcolor: '#7B1FA2' },
                                          minWidth: 0,
                                          px: 1,
                                        }}
                                      >
                                        Edit
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Download Excel template">
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={
                                          downloadingExcelId === item.id ? (
                                            <CircularProgress size={14} sx={{ color: '#CE93D8' }} />
                                          ) : (
                                            <DownloadIcon sx={{ fontSize: 18 }} />
                                          )
                                        }
                                        disabled={downloadingExcelId === item.id}
                                        onClick={(e) => handleDownloadExcel(e, item)}
                                        sx={{
                                          borderColor: '#9C27B0',
                                          color: '#E1BEE7',
                                          minWidth: 0,
                                          px: 1,
                                          '&:hover': {
                                            borderColor: '#CE93D8',
                                            bgcolor: 'rgba(156,39,176,0.12)',
                                          },
                                        }}
                                      >
                                        Excel
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Download original Word test script from bucket">
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={
                                          downloadingOriginalDocxId === item.id ? (
                                            <CircularProgress size={14} sx={{ color: '#90CAF9' }} />
                                          ) : (
                                            <ArticleIcon sx={{ fontSize: 18 }} />
                                          )
                                        }
                                        disabled={downloadingOriginalDocxId === item.id}
                                        onClick={(e) => handleDownloadOriginalDocx(e, item)}
                                        sx={{
                                          borderColor: '#1565C0',
                                          color: '#BBDEFB',
                                          minWidth: 0,
                                          px: 1,
                                          '&:hover': {
                                            borderColor: '#90CAF9',
                                            bgcolor: 'rgba(21,101,192,0.12)',
                                          },
                                        }}
                                      >
                                        Original Script
                                      </Button>
                                    </Tooltip>
                                    <Tooltip title="Download project Word script (out-of-scope steps removed)">
                                      <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={
                                          downloadingDocxId === item.id ? (
                                            <CircularProgress size={14} color="inherit" />
                                          ) : (
                                            <ArticleIcon sx={{ fontSize: 18 }} />
                                          )
                                        }
                                        disabled={downloadingDocxId === item.id}
                                        onClick={(e) => handleDownloadDocx(e, item)}
                                        sx={{
                                          bgcolor: '#00695C',
                                          '&:hover': { bgcolor: '#004D40' },
                                          minWidth: 0,
                                          px: 1,
                                        }}
                                      >
                                        Project Script
                                      </Button>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                  <Chip
                                    label={item.module}
                                    size="small"
                                    sx={{
                                      borderColor: REALIZE_COLOR,
                                      color: '#E1BEE7',
                                      bgcolor: 'rgba(156,39,176,0.12)',
                                    }}
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 500, verticalAlign: 'top' }}>
                                  {item.scope_item}
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                  {item.scope_item_description}
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={filteredScopeItems.length}
                      page={mainTablePage}
                      onPageChange={handleMainTablePageChange}
                      rowsPerPage={MAIN_TABLE_ROWS_PER_PAGE}
                      rowsPerPageOptions={[MAIN_TABLE_ROWS_PER_PAGE]}
                      labelRowsPerPage="Rows per page"
                      sx={{
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderTop: 0,
                        borderRadius: '0 0 4px 4px',
                        color: 'text.secondary',
                      }}
                    />
                  </>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={previewDialog.open}
        onClose={handleClosePreviewDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#424242',
            color: '#FFFFFF',
            minHeight: '72vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Test Procedure Preview
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {previewItem?.scope_item} · {previewItem?.scope_item_description}
              </Typography>
            </Box>
            {previewItem?.module && (
              <Chip
                label={previewItem.module}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: REALIZE_COLOR,
                  color: '#E1BEE7',
                  bgcolor: 'rgba(156,39,176,0.12)',
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {previewDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : Array.isArray(previewAllRows) && previewAllRows.length > 0 ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: 2,
                  flexDirection: { xs: 'column', md: 'row' },
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${previewInScopeCount} of ${previewAllRows.length} in scope`}
                    size="small"
                    color={previewInScopeCount > 0 ? 'success' : 'default'}
                    variant={previewInScopeCount > 0 ? 'filled' : 'outlined'}
                  />
                  <Chip
                    label={`${previewOutScopeCount} out of scope`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(event) => handleOpenEditDialog(event, previewItem)}
                    sx={{ bgcolor: REALIZE_COLOR, '&:hover': { bgcolor: '#7B1FA2' } }}
                  >
                    Edit Scenario Scope
                  </Button>
                  <Button
                    component="label"
                    variant="outlined"
                    size="small"
                    startIcon={
                      uploadingScopeExcelId === previewItem?.id ? (
                        <CircularProgress size={14} sx={{ color: '#CE93D8' }} />
                      ) : (
                        <UploadIcon />
                      )
                    }
                    disabled={uploadingScopeExcelId === previewItem?.id}
                    sx={{
                      borderColor: '#9C27B0',
                      color: '#E1BEE7',
                      '&:hover': {
                        borderColor: '#CE93D8',
                        bgcolor: 'rgba(156,39,176,0.12)',
                      },
                    }}
                  >
                    Upload Scope Excel
                    <input
                      hidden
                      type="file"
                      accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={(event) => handleUploadScopeExcel(event, previewItem)}
                    />
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    disabled={downloadingExcelId === previewItem?.id}
                    onClick={(event) => handleDownloadExcel(event, previewItem)}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArticleIcon />}
                    disabled={downloadingOriginalDocxId === previewItem?.id}
                    onClick={(event) => handleDownloadOriginalDocx(event, previewItem)}
                  >
                    Original Script
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ArticleIcon />}
                    disabled={downloadingDocxId === previewItem?.id}
                    onClick={(event) => handleDownloadDocx(event, previewItem)}
                  >
                    Project Script
                  </Button>
                </Box>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '52vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="12%">Step</TableCell>
                      <TableCell width="68%">Test procedures</TableCell>
                      <TableCell width="20%" align="center">
                        Scenario in scope
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell sx={{ verticalAlign: 'top', fontFamily: 'monospace' }}>
                          {row.step_ref}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
                          {row.test_procedure}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={row.scenario_in_scope ? 'In scope' : 'Out of scope'}
                            color={row.scenario_in_scope ? 'success' : 'default'}
                            variant={row.scenario_in_scope ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {previewAllRows.length > PREVIEW_TABLE_ROWS_PER_PAGE && (
                <TablePagination
                  component="div"
                  count={previewAllRows.length}
                  page={previewPage}
                  onPageChange={(_event, newPage) => handlePreviewTablePageChange(previewItem.id, newPage)}
                  rowsPerPage={PREVIEW_TABLE_ROWS_PER_PAGE}
                  rowsPerPageOptions={[PREVIEW_TABLE_ROWS_PER_PAGE]}
                  labelRowsPerPage="Rows per page"
                  sx={{
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderTop: 0,
                    borderRadius: '0 0 4px 4px',
                    color: 'text.secondary',
                  }}
                />
              )}
            </>
          ) : (
            <Alert severity="info">No test procedures are loaded for this scope item.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClosePreviewDialog}>Close</Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            disabled={!previewItem?.id || previewDialogLoading}
            onClick={(event) => handleOpenEditDialog(event, previewItem)}
            sx={{ bgcolor: REALIZE_COLOR, '&:hover': { bgcolor: '#7B1FA2' } }}
          >
            Edit Scenario Scope
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialog.open}
        onClose={handleCloseEditDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            maxHeight: '88vh',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Edit Test Script Scope
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editDialog.item?.scope_item} · {editDialog.item?.scope_item_description}
              </Typography>
            </Box>
            {editDialog.item?.module && (
              <Chip
                label={editDialog.item.module}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: REALIZE_COLOR,
                  color: '#E1BEE7',
                  bgcolor: 'rgba(156,39,176,0.12)',
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {editDialogLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : editRows.length > 0 ? (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: 2,
                  flexDirection: { xs: 'column', md: 'row' },
                  mb: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Select the test procedures that are in scope for this project. Parent and child steps follow the same hierarchy behavior as the current table.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${editSelectedCount} of ${editRows.length} in scope`}
                    color={editSelectedCount > 0 ? 'success' : 'default'}
                    variant={editSelectedCount > 0 ? 'filled' : 'outlined'}
                  />
                  <Chip
                    label={`${editChangedCount} changed`}
                    color={editChangedCount > 0 ? 'warning' : 'default'}
                    variant={editChangedCount > 0 ? 'filled' : 'outlined'}
                    onClick={editChangedCount > 0 ? () => setEditScopeFilter('changed') : undefined}
                  />
                </Box>
              </Box>

              <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search"
                    placeholder="Search step or test procedure"
                    value={editSearchQuery}
                    onChange={(event) => setEditSearchQuery(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="uat-edit-scope-filter-label">Scope</InputLabel>
                    <Select
                      labelId="uat-edit-scope-filter-label"
                      value={editScopeFilter}
                      label="Scope"
                      onChange={(event) => setEditScopeFilter(event.target.value)}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="in">In scope</MenuItem>
                      <MenuItem value="out">Out of scope</MenuItem>
                      <MenuItem value="changed">Changed only</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SelectAllIcon />}
                      disabled={
                        savingEdit ||
                        filteredEditRows.length === 0 ||
                        filteredEditSelectedCount === filteredEditRows.length
                      }
                      onClick={() => handleBulkEditScenarioChange(true)}
                    >
                      Select All
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ClearAllIcon />}
                      disabled={
                        savingEdit ||
                        filteredEditRows.length === 0 ||
                        filteredEditSelectedCount === 0
                      }
                      onClick={() => handleBulkEditScenarioChange(false)}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {editError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {editError}
                </Alert>
              )}

              {scopeImportSummary && (
                <Alert
                  severity={scopeImportSummary.unmatchedCount || scopeImportSummary.missingCount ? 'warning' : 'success'}
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {scopeImportSummary.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scopeImportSummary.filename}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`${scopeImportSummary.matchedCount} matched`} />
                      <Chip size="small" color="warning" variant="outlined" label={`${scopeImportSummary.changedCount} changed`} />
                      <Chip size="small" color="success" variant="outlined" label={`${scopeImportSummary.inScopeCount} in scope`} />
                      <Chip size="small" variant="outlined" label={`${scopeImportSummary.outScopeCount} out of scope`} />
                      {scopeImportSummary.unmatchedCount > 0 && (
                        <Chip size="small" color="warning" label={`${scopeImportSummary.unmatchedCount} unmatched`} />
                      )}
                      {scopeImportSummary.missingCount > 0 && (
                        <Chip size="small" color="warning" label={`${scopeImportSummary.missingCount} missing`} />
                      )}
                    </Box>
                  </Box>
                </Alert>
              )}

              {scopeImportIssue && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'grid', gap: 1 }}>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {scopeImportIssue.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {scopeImportIssue.filename}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" color="error" label={`${scopeImportIssue.invalidValues.length} invalid`} />
                      <Chip size="small" color="warning" variant="outlined" label={`${scopeImportIssue.unmatchedSteps.length} unmatched`} />
                      <Chip size="small" variant="outlined" label={`${scopeImportIssue.missingSteps.length} missing`} />
                    </Box>
                    {scopeImportIssue.invalidValues.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Invalid values
                        </Typography>
                        {scopeImportIssue.invalidValues.slice(0, 5).map((entry) => (
                          <Typography key={`${entry.excel_row}-${entry.step_ref}`} variant="caption" component="div">
                            Row {entry.excel_row}, step {entry.step_ref || '-'}: {entry.value || '-'}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {scopeImportIssue.unmatchedSteps.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          Unmatched Excel steps
                        </Typography>
                        <Typography variant="caption" component="div">
                          {scopeImportIssue.unmatchedSteps.slice(0, 8).map((entry) => entry.step_ref).join(', ')}
                          {scopeImportIssue.unmatchedSteps.length > 8 ? '...' : ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Alert>
              )}

              {filteredEditRows.length === 0 ? (
                <Alert severity="info">No test procedures match the current search or filter.</Alert>
              ) : (
                <>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '50vh' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell width="12%">Step</TableCell>
                          <TableCell width="68%">Test procedures</TableCell>
                          <TableCell width="20%" align="center">
                            Scenario in scope
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedEditRows.map((row) => {
                          const rowChanged =
                            editBaselineById.has(row.id) &&
                            Boolean(row.scenario_in_scope) !== editBaselineById.get(row.id);

                          return (
                            <TableRow
                              key={row.id}
                              hover
                              onClick={() => handleEditScenarioChange(row, !Boolean(row.scenario_in_scope))}
                              sx={{
                                cursor: savingEdit ? 'default' : 'pointer',
                                bgcolor: rowChanged ? 'rgba(255,152,0,0.10)' : undefined,
                                '&:hover': {
                                  bgcolor: rowChanged ? 'rgba(255,152,0,0.16)' : undefined,
                                },
                                '& > *': {
                                  borderColor: rowChanged ? 'rgba(255,183,77,0.35)' : undefined,
                                },
                              }}
                            >
                              <TableCell
                                sx={{
                                  verticalAlign: 'top',
                                  fontFamily: 'monospace',
                                  borderLeft: rowChanged ? '3px solid #FFB74D' : '3px solid transparent',
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                  <span>{row.step_ref}</span>
                                  {rowChanged && (
                                    <Chip
                                      label="Modified"
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ fontFamily: 'inherit' }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'pre-wrap' }}>
                                {row.test_procedure}
                              </TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={Boolean(row.scenario_in_scope)}
                                  disabled={savingEdit}
                                  onClick={(event) => event.stopPropagation()}
                                  onChange={(event) => handleEditScenarioChange(row, event.target.checked)}
                                  inputProps={{
                                    'aria-label': `Scenario in scope for step ${row.step_ref}`,
                                  }}
                                  sx={{
                                    color: REALIZE_COLOR,
                                    '&.Mui-checked': { color: REALIZE_COLOR },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    component="div"
                    count={filteredEditRows.length}
                    page={editTablePage}
                    onPageChange={(_event, newPage) => setEditTablePage(newPage)}
                    rowsPerPage={EDIT_TABLE_ROWS_PER_PAGE}
                    rowsPerPageOptions={[EDIT_TABLE_ROWS_PER_PAGE]}
                    labelRowsPerPage="Rows per page"
                    sx={{
                      border: '1px solid rgba(255,255,255,0.12)',
                      borderTop: 0,
                      borderRadius: '0 0 4px 4px',
                      color: 'text.secondary',
                    }}
                  />
                </>
              )}
            </>
          ) : (
            <Alert severity="info">No test procedures are loaded for this scope item.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            startIcon={<RestoreIcon />}
            onClick={handleResetEditChanges}
            disabled={savingEdit || editChangedCount === 0}
          >
            Reset Changes
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleCloseEditDialog} disabled={savingEdit}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={savingEdit ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
            disabled={savingEdit || editRows.length === 0 || editDialogLoading || editChangedCount === 0}
            onClick={handleSaveEditDialog}
            sx={{ bgcolor: REALIZE_COLOR, '&:hover': { bgcolor: '#7B1FA2' } }}
          >
            {savingEdit
              ? 'Saving...'
              : editChangedCount === 1
                ? 'Save 1 Change'
                : `Save ${editChangedCount} Changes`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={configDialog.open} onClose={() => setConfigDialog((c) => ({ ...c, open: false }))} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edition and version required</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{configDialog.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfigDialog((c) => ({ ...c, open: false }))}>Close</Button>
          <Button
            variant="contained"
            onClick={() => navigate(`/projects/${currentProjectId}/overview/scope`)}
            sx={{ bgcolor: '#9C27B0', '&:hover': { bgcolor: '#7B1FA2' } }}
          >
            Go to Project Scope
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        action={
          snackbar.severity === 'warning' ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/projects/${currentProjectId}/overview/scope`)}
            >
              Go to Project Scope
            </Button>
          ) : null
        }
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'error'
                ? 'error.main'
                : snackbar.severity === 'warning'
                  ? 'warning.main'
                  : 'success.main',
          },
        }}
      />
    </Box>
  );
};

async function getScopeItemsForProject(projectId) {
  const isLocalDevelopment =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname.startsWith('192.168.') ||
    window.location.hostname.startsWith('10.') ||
    window.location.hostname === '[::1]';
  const apiBaseUrl = isLocalDevelopment
    ? `${window.location.protocol}//${window.location.hostname}:5001/api`
    : `${window.location.protocol}//${window.location.hostname}/api`;

  const response = await fetch(`${apiBaseUrl}/workshops/scope-items/${projectId}`, {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch scope items');
  }

  const data = await response.json();

  if (Array.isArray(data)) {
    return data;
  }
  if (data?.scope_items && Array.isArray(data.scope_items)) {
    return data.scope_items;
  }
  if (data?.items && Array.isArray(data.items)) {
    return data.items;
  }
  return [];
}

export default UATTestScriptsPage;
